// =============================================================================
// campaign-analysis-client.ts — Client API ADMIN du read-model CampaignAnalysis (S17 / D1),
// DOUBLABLE.
// =============================================================================
// RÉVISION G0-S17-17 : la vue admin = MÉTADONNÉES de la `CampaignAnalysis` + SNAPSHOT ANONYMISÉ
// (percentiles consolidés winsorized par couple). Le RANKING NOMINATIF DÉTAILLÉ (rang, raison
// sociale, SIRET, TJM individuel par fournisseur) est RETIRÉ — reporté à S17-bis. Ce client
// n'expose donc QUE le read-model anonymisé (percentiles + métadonnées).
//
// Miroir de l'endpoint ADMIN de S17/D1 (read-model de la `CampaignAnalysis`, produite POST-clôture
// définitive par le worker : winsorization IQR + percentiles type 7 — DBU §2.6). Comme les autres
// clients UI (operational-view, rate-collection…), expose une INTERFACE (CampaignAnalysisClient)
// consommée par les hooks TanStack ; l'impl HTTP réelle (fetch) vient derrière la MÊME interface
// (DI seam, stub-first), substituée sans toucher l'UI.
//
// AUTORITÉ = API/worker : les percentiles consolidés (P10/P25/P50/P75/P90/MIN/MAX winsorized) sont
// CALCULÉS en amont (moteur @jeko/analytics-engine, golden master S16) et LUS ici. L'UI ne porte
// AUCUNE règle métier : elle LIT et habille. Le campaignId est TOUJOURS la ressource d'URL (jamais
// dans une query). La route est un GET (l'admin LIT, jamais d'écriture — invariant n°1) ⇒ AUCUN
// en-tête CSRF requis.
//
//   GET /admin/positioning/:campaignId/percentiles (F) → AnalysisPercentilesResponseDto
//
// DTO = miroirs HTTP ASCII (PAS @jeko/db) : l'enum de séniorité est répliqué en union de littéraux
// (parité Prisma RateSeniorityLevel). Horodatages = ISO 8601 (string), jamais d'objet Date ORM.

// --- Enum miroir HTTP (parité Prisma RateSeniorityLevel, ASCII — accents = i18n front) ---

import { HttpApiError } from '@/api/http-error';

/** Niveau de séniorité (parité Prisma RateSeniorityLevel). */
export type RateSeniorityLevelDto = 'Debutant' | 'Maitrisant' | 'Expert' | 'Referent';

/**
 * État machine de la `CampaignAnalysis` (DBU §2.6, ADR D10) :
 *   EnAttente → EnTraitement → Disponible → Validee → Publiee (pas d'état Obsolete).
 * Le read-model n'expose le détail qu'à partir de `Disponible` (sinon traitement en cours).
 */
export type AnalysisStateDto =
  | 'EnAttente'
  | 'EnTraitement'
  | 'Disponible'
  | 'Validee'
  | 'Publiee';

// NOTE G0-S17-17 : le RANKING NOMINATIF DÉTAILLÉ (rang, raison sociale, SIRET, TJM individuel) est
// RETIRÉ de cette vue — reporté à S17-bis. Aucun DTO de ranking nominatif n'est plus exposé ici.

// =============================================================================
// (B) Percentiles consolidés par couple (DBU §2.6 « rapport de traitement »). Miroir D1. ANONYMISÉ.
// =============================================================================

/**
 * Percentiles consolidés WINSORIZED (type 7) d'un couple profil×séniorité (DBU §2.6 / EF-BEN-3→7).
 * Miroir EXACT de l'API `AnalysisPercentileRowDto` (apps/api/src/analytics/dto/positioning.dto.ts).
 * Les valeurs `*Eur` + `medianEur` sont des entiers EUR ou `null` ; MIN/MAX sont WINSORISÉES.
 * `count` (effectif retenu) est admin-only (G0-S17-3, jamais au fournisseur). Sous le gate K-anon,
 * `suppressed:true` et TOUTES les valeurs numériques (bornes, percentiles, médiane, count) sont `null`.
 */
export interface AnalysisPercentileRowDto {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  readonly minEur: number | null;
  readonly p10Eur: number | null;
  readonly p25Eur: number | null;
  readonly p50Eur: number | null;
  readonly p75Eur: number | null;
  readonly p90Eur: number | null;
  readonly maxEur: number | null;
  /** Médiane winsorisée (median_eur) — admin-only (DBU §2.6). null si supprimée. */
  readonly medianEur: number | null;
  /** Effectif retenu (count_winsorized) — INTERNE ADMIN (G0-S17-3, jamais au fournisseur). null si supprimée. */
  readonly count: number | null;
  /** true ⇒ ligne supprimée sous le gate K-anon (toutes les valeurs numériques = null). */
  readonly suppressed: boolean;
}

/** Réponse (B) GET percentiles consolidés (selon filtres F). */
export interface AnalysisPercentilesResponseDto {
  readonly campaignId: string;
  readonly analysisState: AnalysisStateDto;
  /** Version de catalogue figée utilisée par le calcul (DBU §2.6, traçabilité). */
  readonly catalogVersionId: string;
  readonly rows: readonly AnalysisPercentileRowDto[];
}

// =============================================================================
// (B) Comparaison avec les campagnes ANTÉRIEURES (V-ANALYSIS-COMPARE, DBU §2.5 l.1223-1225). ANONYMISÉ.
// =============================================================================

/**
 * Une analyse ANTÉRIEURE projetée pour la comparaison. Miroir EXACT de l'API `PriorAnalysisComparisonDto`.
 * Même surface anonyme que le rapport consolidé (7 bornes WINSORISÉES par couple, `suppressed` par analyse).
 * `catalogVersionId` = DRAPEAU de comparabilité : l'UI le compare au catalogue de la campagne COURANTE
 * (elle le connaît via useCampaignDetail) — AUCUNE jointure cross-schéma côté serveur (invariant n°2).
 */
export interface PriorAnalysisComparisonDto {
  readonly campaignId: string;
  readonly analysisState: AnalysisStateDto;
  readonly catalogVersionId: string;
  readonly rows: readonly AnalysisPercentileRowDto[];
}

/**
 * Réponse (B) GET comparaison campagnes antérieures. `campaignId` = campagne COURANTE (server-set). `priors`
 * = analyses des campagnes antérieures ÉLIGIBLES (même clientSiren, Publiee) QUI ONT une analyse consolidée
 * (les autres sont omises côté serveur). Miroir EXACT de l'API `AnalysisComparisonResponseDto`.
 */
export interface AnalysisComparisonResponseDto {
  readonly campaignId: string;
  readonly priors: readonly PriorAnalysisComparisonDto[];
}

// =============================================================================
// (B) MÉTADONNÉES de l'analyse (V26-ANALYSE-UI) — endpoint DÉJÀ exposé GET /analysis (computedAt). Miroir
//     RESTREINT de l'API `CampaignAnalysisDto`. ADDITIF : nécessaire pour la « date de calcul » (DBU §2.6
//     l.1345) — le contrat percentiles ne porte PAS de date (O2 du cadrage). AUCUNE modif API.
// =============================================================================

/**
 * Métadonnées de la `CampaignAnalysis` d'une campagne post-clôture (miroir RESTREINT de l'API
 * `CampaignAnalysisDto`). `computedAt` = date de calcul de l'analyse (DBU §2.6 l.1345, traçabilité du
 * rapport). Horodatages ISO 8601 (string), jamais un objet Date ORM. Admin-only (aucune PII fournisseur).
 */
export interface CampaignAnalysisMetaDto {
  readonly analysisId: string;
  readonly campaignId: string;
  readonly catalogVersionId: string;
  readonly state: AnalysisStateDto;
  /** Date de calcul de l'analyse (DBU §2.6 l.1345). ISO 8601. */
  readonly computedAt: string;
  readonly validatedAt: string | null;
  readonly publishedAt: string | null;
  readonly kAnonymityThreshold: number;
}

// =============================================================================
// (B) Référentiel « stack technologique » (grain FAMILLE) — V26-ANALYSE-UI / DEC-17 (Option A).
// =============================================================================

/** Correspondance profil → famille (grain famille) — le « tag » stack remonte par la famille. Miroir API. */
export interface CampaignStackProfileDto {
  readonly profileCode: string;
  readonly famille: string;
  /**
   * A1 (fusion analyse/rang, 2026-09-17) — SOUS-FAMILLE du profil (catalogue FIGÉ de l'analyse) : alimente
   * le filtre « Sous-famille » de la page fusionnée. `null`/absent ⇒ le profil ne porte pas de sous-famille
   * (le filtre est inerte sur lui). OPTIONNEL côté miroir pour ne casser aucun double existant.
   */
  readonly sousFamille?: string | null;
}

/**
 * Référentiel « stack » (grain FAMILLE) d'une campagne (DBU §2.6 l.1353, DEC-17 Option A). Miroir EXACT de
 * l'API `CampaignStackReferentialDto`. LECTURE D'AFFICHAGE SÉPARÉE : composée du référentiel de compétences
 * spécifiques §2.10 (SpecificSkill + SkillFamilyAssoc) côté API. `stacks` = familles portées par ≥1
 * compétence ACTIVE (Archived filtré) ; `profiles` = profil → famille de tous les profils de la version.
 * L'UI filtre l'affichage : options = `stacks`, tag d'une ligne = famille du profil via `profiles`.
 */
export interface CampaignStackReferentialDto {
  readonly campaignId: string;
  readonly catalogVersionId: string;
  readonly stacks: readonly string[];
  readonly profiles: readonly CampaignStackProfileDto[];
}

// =============================================================================
// (B) NUAGE DE POINTS (V26-NUAGE, DBU §2.6 l.1365-1379) — read-model NOMINATIF admin. Miroir de l'API
//     `ScatterPointDto` (apps/api/src/analytics/dto/points.dto.ts). GRAIN TRINÔME (siret×profil×niveau) à
//     TJM retenu NON-NULL. VUE ADMIN NOMINATIVE (SIRET + raison sociale), SANS masquage K-anon (PLS-08 —
//     le gate protège UNIQUEMENT Pulse). L'OVERLAY des percentiles (P10..P90) vient de getPercentiles
//     (SOURCE DISTINCTE, agrégat anonyme, gate K-anon) : point nominatif visible ⇄ borne overlay masquable.
// =============================================================================

/** Un point du nuage (grain trinôme). `tjmRetenuEur` GARANTI non-null (un point exige un Y). Miroir API. */
export interface ScatterPointDto {
  readonly supplierSiret: string;
  readonly raisonSociale: string;
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  /** Famille du profil (couleur du point) — jointure GAUCHE catalog figé. `null` si profil hors catalogue. */
  readonly family: string | null;
  /** TJM retenu (€) — coordonnée Y. Cycle 2 si présent, sinon Cycle 1. TOUJOURS non-null. */
  readonly tjmRetenuEur: number;
}

/** Réponse GET nuage de points d'une campagne (admin-only, nominatif §2.6). Miroir API. */
export interface ScatterPointsResponseDto {
  readonly campaignId: string;
  readonly points: readonly ScatterPointDto[];
}

// =============================================================================
// (B) TABLEAU DE FIN DE CAMPAGNE « Prix cible et médianes par cycle » (lot L9, DBU v2.7 §2.6, EF-BEN-12).
//     Miroir EXACT de l'API `CampaignTargetPriceSummaryDto` (apps/api/src/analytics/dto/target-price-summary.dto.ts).
//     NOMINATIF, ADMIN SEUL : une médiane Cn à effectif 1 est le TJM d'un fournisseur identifiable. Surface
//     SÉPARÉE de getPercentiles (agrégat anonyme) ; jamais exposée fournisseur ni Pulse.
// =============================================================================

/** Origine du prix cible final (valeur Pulse acceptée ou saisie admin). */
export type TargetPriceSourceDto = 'PulseDefault' | 'AdminEntry';

/**
 * Statut d'un cycle pour un couple : `NotApplicable` = cycle inexistant (C2 d'une campagne mono-cycle, afficher
 * « sans objet ») ; `NoResponse` = cycle existant sans contributeur (afficher 0 réponse) ; `Available` = médiane
 * renseignée.
 */
export type CycleMedianStatusDto = 'NotApplicable' | 'NoResponse' | 'Available';

/** Une ligne par couple Profil × Niveau du périmètre. Valeur absente ⇒ `null` (jamais 0). Miroir API. */
export interface TargetPriceSummaryRowDto {
  /** Famille du profil (catalogue figé de l'analyse) ; `null` si profil hors catalogue. */
  readonly family: string | null;
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  readonly targetPriceEur: number | null;
  readonly priceSource: TargetPriceSourceDto | null;
  /** Valeur proposée par défaut (P50 Pulse) au moment de la pose. */
  readonly defaultEurProposed: number | null;
  /** Date de pose du prix (ISO 8601). */
  readonly validatedAt: string | null;
  readonly medianC1Eur: number | null;
  readonly countC1: number;
  readonly c1Status: CycleMedianStatusDto;
  readonly medianC2Eur: number | null;
  /** `null` si C2 sans objet (mono-cycle), 0 si aucune réponse. */
  readonly countC2: number | null;
  readonly c2Status: CycleMedianStatusDto;
  /** Écart `médiane C2 - prix cible` (€) ; `null` si l'un manque. */
  readonly gapMedianC2VsTargetEur: number | null;
  /** Même écart en % du prix cible (entier) ; `null` si indéfini. */
  readonly gapMedianC2VsTargetPct: number | null;
}

/** Réponse GET /admin/positioning/:campaignId/target-price-summary. Lignes déjà triées (famille, profil, niveau). */
export interface CampaignTargetPriceSummaryDto {
  readonly campaignId: string;
  readonly analysisState: AnalysisStateDto;
  /** Date du calcul consolidé (ISO 8601). */
  readonly computedAt: string;
  /** 1 = mono-cycle (C2 sans objet), 2 = bi-cycle. */
  readonly finalCycleNumber: number;
  readonly rows: readonly TargetPriceSummaryRowDto[];
}

// --- Filtres serveur (F) ------------------------------------------------------

/** Filtres serveur communs (F). Tous optionnels ; l'API valide (fail-closed). */
export interface CampaignAnalysisFilters {
  readonly profileCode?: string;
  readonly seniorityLevel?: RateSeniorityLevelDto;
}

/**
 * Erreur applicative renvoyée par l'API admin campaign-analysis (HTTP 4xx/5xx). L'UI l'affiche
 * telle quelle ; elle ne réinterprète JAMAIS la règle métier (autorité = API/worker).
 */
export class CampaignAnalysisApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'CampaignAnalysisApiError');
  }
}

/**
 * Contrat du client read-model ADMIN de la `CampaignAnalysis` — miroir du GET percentiles de D1
 * (AdminSessionGuard). Aucune règle métier ici : les percentiles consolidés sont l'autorité de
 * l'API/worker. L'UI relaie l'intention (campaignId d'URL + filtres F) et habille. RÉVISION
 * G0-S17-17 : le ranking nominatif détaillé est RETIRÉ (reporté S17-bis) — plus de `getRanking`.
 */
export interface CampaignAnalysisClient {
  /** (B) GET percentiles consolidés winsorized (P10/P25/P50/P75/P90/MIN/MAX) par couple (ANONYMISÉ). */
  getPercentiles(
    campaignId: string,
    filters: CampaignAnalysisFilters,
  ): Promise<AnalysisPercentilesResponseDto>;

  /**
   * (B) GET comparaison avec les campagnes ANTÉRIEURES (V-ANALYSIS-COMPARE). `priorCampaignIds` = les
   * campagnes antérieures ÉLIGIBLES (même clientSiren, Publiee) résolues EN AMONT côté front — l'API ne
   * résout PAS l'éligibilité (invariant n°2 : pas de jointure analytics↔campaign). Réponse : les analyses
   * de celles qui possèdent une analyse consolidée (les autres omises). Liste vide ⇒ `priors: []`.
   */
  getAnalysisComparison(
    campaignId: string,
    priorCampaignIds: readonly string[],
    filters: CampaignAnalysisFilters,
  ): Promise<AnalysisComparisonResponseDto>;

  /**
   * (B) V26-ANALYSE-UI — MÉTADONNÉES de l'analyse (GET /analysis, endpoint DÉJÀ exposé) : `computedAt` (date
   * de calcul, DBU §2.6 l.1345) + état + version de catalogue. ADDITIF : le contrat percentiles ne porte pas
   * de date ; c'est la SEULE source propre de la date de calcul (O2). 404 si aucune analyse consolidée.
   */
  getAnalysis(campaignId: string): Promise<CampaignAnalysisMetaDto>;

  /**
   * (B) V26-ANALYSE-UI / DEC-17 — RÉFÉRENTIEL « stack » (grain FAMILLE) du filtre d'analyse (GET /stacks).
   * Lecture d'affichage SÉPARÉE (§2.10 SpecificSkill + SkillFamilyAssoc côté API). `stacks` = familles
   * portées par ≥1 compétence ACTIVE (Archived filtré) ; `profiles` = profil → famille. 404 si aucune analyse.
   */
  getStacks(campaignId: string): Promise<CampaignStackReferentialDto>;

  /**
   * (B) V26-NUAGE — NUAGE DE POINTS (GET /points, DBU §2.6 l.1365-1379) : grain TRINÔME nominatif (x=séniorité,
   * y=TJM, couleur=famille). VUE ADMIN NOMINATIVE, SANS K-anon (PLS-08). Les trinômes sans réponse TJM sont
   * EXCLUS côté serveur (un point exige un Y). L'overlay des percentiles est lu SÉPARÉMENT (getPercentiles).
   * 404 si aucune analyse consolidée.
   */
  getPoints(campaignId: string): Promise<ScatterPointsResponseDto>;

  /**
   * (B) L9 — TABLEAU DE FIN DE CAMPAGNE « Prix cible et médianes par cycle » (GET /target-price-summary, DBU
   * §2.6, EF-BEN-12). NOMINATIF admin seul, distinct des percentiles anonymes. 404 si aucune analyse consolidée.
   */
  getTargetPriceSummary(campaignId: string): Promise<CampaignTargetPriceSummaryDto>;
}
